<template>
  <DefaultLayout v-if="showAuditPage">
    <vue-headful :title="getPageTitle('Data Collection Period Detail')" />
    <template v-if="showDataCollectionTemplate">
      <DataCollectionModal
        :auditDetail="auditDetail"
        :auditId="auditId"
        :singleAuditCriteriaId="singleAuditCriteriaId"
        :editDataCollectionRecord="editDataCollectionRecord"
        :dataCollectionSummary="dataCollectionSummary"
        @closeDataCollectionTemplate="closeDataCollectionTemplate"
      />
    </template>
    <template v-else>
      <AuditHeader
        :auditDetail="auditDetail"
        :auditSettingInfo="auditSettingInfo"
      />
      <article
        class="tile notification bg-primary-light p1"
        v-if="isAuditArchived"
      >
        <small>{{ message }}</small>
      </article>

      <b-tabs
        @input="tabChange"
        :class="$style.tabLabel"
        v-model="currentTab"
        :animated="false"
        :destroy-on-hide="true"
      >
        <b-tab-item :class="$style.tabItem" label="DASHBOARD">
          <AuditDashboardTab
            v-if="!isLoading && auditDetail"
            :auditDetail="auditDetail"
            @changeActiveTab="changeActiveTab"
          />
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="CRITERIA">
          <AuditCriteriaTab
            v-if="!isLoading && showCriteriaTab"
            :auditId="auditId"
            :auditDetail="auditDetail"
            :editable="editable"
            :isAuditCycleCommencedOrCompleted="isAuditCycleCommencedOrCompleted"
          />
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="PARTICIPANTS">
          <AuditParticipantTab
            v-if="!isLoading"
            :auditDetail="auditDetail"
            :projectOwner="projectOwner"
            :editable="editable"
          />
        </b-tab-item>

        <b-tab-item
          :class="$style.tabItem"
          label="SAMPLING"
          :visible="
            auditDetail ? auditDetail.isUserAuthorizedToAccessAllSites : false
          "
        >
          <div :class="$style.projectSamplingEditContainer">
            <AuditSampling
              v-if="!isLoading && showSamplingTab && auditDetail"
              :auditId="auditId"
              :auditDataCollectionDate="auditDetail.startedAt"
              :auditCategory="auditDetail.auditCategory"
              :editable="editable"
            />
          </div>
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="DATA COLLECTION">
          <DataCollectionTab
            v-if="showDataCollectionTab"
            :editable="editable"
            :key="dataCollectionTabKey"
            :auditId="auditId"
            @showDataCollectionTemplate="handleShowDataCollectionTemplate"
            @editDataCollectionRecord="handleEditDataCollectionRecord"
          />
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="GRIP">
          <GripTab
            v-if="!isProjectDetailsLoading && showGripTab"
            :key="auditGripTabKey"
            :projectDetail="projectDetail"
            :editable="editable"
            :projectId="projectDetail.id"
            @changeActiveTab="changeActiveTab"
          >
          </GripTab>
          <div :class="$style.noDataMessageContainer">
            <div :class="$style.noDataMessage"></div>
          </div>
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="REPORT">
          <ReportsTab
            v-if="!isLoading && showReportTab"
            :key="reportTabKey"
            :auditDetail="auditDetail"
          ></ReportsTab>
          <div v-else :class="$style.noDataMessageContainer">
            <div :class="$style.noDataMessage">
              Reports under development for multi sites project.
            </div>
          </div>
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="SETTINGS">
          <AuditSettingsTab
            v-if="!isLoading && showSettingsTab"
            :key="settingsTabKey"
            :auditDetail="auditDetail"
            :auditId="auditId"
            :auditSettingInfo="auditSettingInfo"
            @updateAuditSettings="updateAuditSettingInfo"
            @updatedAuditStatus="handleUpdatedAuditStatus"
            :isAuditCycleCommencedOrCompleted="isAuditCycleCommencedOrCompleted"
          />
        </b-tab-item>

        <b-tab-item :class="$style.tabItem" label="ACTIVITY LOG">
          <div :class="$style.activityLogContainer">
            <AuditEventLogsTab
              v-if="!isLoading"
              :auditDetail="auditDetail"
              :auditId="auditId"
            ></AuditEventLogsTab>
          </div>
        </b-tab-item>
      </b-tabs>
    </template>
  </DefaultLayout>
</template>
<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import {
  AuditDetail,
  AuditStatus,
} from '@/store/modules/audits/types/audits.types';
import CriteriaCard from '@/views/Audit/CriteriaCard.vue';
import AuditSampling from '@/views/Audit/AuditSampling.vue';
import {
  UserEntity,
  UserRoleInAudit,
} from '@/store/modules/projects/types/projects.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import AuditCriteriaList from '@/views/Project/components/AuditCriteriaList.vue';
import SiteSettings from '@/views/Project/components/SiteSettings.vue';
import DataCollectionModal from '@/views/Audit/dataCollectionModal.vue';

import AuditHeader from '@/views/Audit/components/AuditHeader.vue';
import AuditCriteriaTab from '@/views/Audit/components/AuditCriteriaTab.vue';
import DataCollectionTab from '@/views/Audit/components/DataCollectionTab.vue';
import ReportsTab from '@/views/Audit/components/ReportsTab.vue';
import AuditDashboardTab from '@/views/Audit/tabs/AuditDashboardTab.vue';
import AuditParticipantTab from '@/views/Audit/tabs/AuditParticipantTab.vue';
import AuditEventLogsTab from '@/views/Audit/tabs/AuditEventLogsTab.vue';
import { useUserEmail } from '@/utils/user.util';
import AuditSettingsTab from '@/views/Audit/tabs/AuditSettingsTab.vue';
import GripTab from '../Grip/GripTab.vue';
import { ProjectDetailPayload } from '../../store/modules/projects/types/projects.types';
import { RootState } from '@/store/store';

/**
 * Declaration of available tabs in the page
 */
const tabNames = [
  '#dashboard',
  '#criteria',
  '#participants',
  '#sampling',
  '#dataCollection',
  '#grip',
  '#report',
  '#settings',
  '#activityLog',
];

@Component({
  components: {
    AuditEventLogsTab,
    DataCollectionTab,
    AuditCriteriaTab,
    AuditHeader,
    DataCollectionModal,
    AuditSampling,
    DefaultLayout,
    HeroBar,
    CriteriaCard,
    AuditCriteriaList,
    SiteSettings,
    ReportsTab,
    AuditDashboardTab,
    AuditParticipantTab,
    GripTab,
    AuditSettingsTab,
  },
})
export default class AuditPage extends Vue {
  public currentTab = 0;
  public deletedSiteIdsList: number[] = [];
  public perPage = 10;
  public page = 1;
  public renderComponent = false;
  public renderDataCollectionComponent = false;
  public showDataCollectionTemplate: boolean = false;
  public singleAuditCriteriaId?: number;
  public editDataCollectionRecord?: any = null;
  public isSiteSubmitButtonDisabled: boolean = true;
  public auditSettingInfo: any = [];
  public projectOwner: UserEntity | null = null;

  public isAuditArchived: boolean = false;
  public showCriteriaTab: boolean = false;
  public showSamplingTab: boolean = false;
  public showDataCollectionTab: boolean = false;
  public showReportTab: boolean = false;
  public showSettingsTab: boolean = false;
  public showAuditPage: boolean = false;
  public showGripTab: boolean = false;

  public criteriaTabKey: number = Math.floor(Math.random() * 999);
  public samplingTabKey: number = Math.floor(Math.random() * 999);
  public dataCollectionTabKey: number = Math.floor(Math.random() * 999);
  public auditGripTabKey: number = Math.floor(Math.random() * 999);
  public reportTabKey: number = Math.floor(Math.random() * 999);
  public settingsTabKey: number = Math.floor(Math.random() * 999);
  public isLoading: boolean = true;
  public isProjectDetailsLoading: boolean = true;

  get currentRouteHash() {
    return this.$route.hash;
  }

  get projectStatus() {
    return this.auditDetail && this.auditDetail.project
      ? this.auditDetail.project.status
      : '';
  }

  get currentAuditStatus() {
    if (this.auditDetail) {
      return this.auditDetail.status;
    }
    return '';
  }

  get auditStatus() {
    return AuditStatus;
  }

  get editable() {
    if (
      this.projectStatus === 'Archived' ||
      this.currentAuditStatus === AuditStatus.ARCHIVED
    ) {
      return false;
    }
    return true;
  }

  get message() {
    return 'Archived data collection cannot be edited';
  }

  get isAuditCycleCommencedOrCompleted(): boolean {
    return this.auditDetail &&
      this.auditDetail.status === AuditStatus.ACTIVE &&
      (this.auditDetail.startedAt !== null || this.auditDetail.endedAt !== null)
      ? true
      : false;
  }

  @Action('projects/getProjectDetail')
  public getProjectDetail!: (projectId: number) => void;

  @State((state: RootState) => state.projects.projectDetail)
  public projectDetail!: ProjectDetailPayload;

  @State((state: RootState) => state.projects.apiState.getProjectDetail.success)
  public getProjectDetailApiStateSuccess!: boolean;

  @Action('audits/getAuditDetail')
  public getAuditDetail!: (auditId: number) => void;

  @Action('audits/getUpdatedAuditJBICriteriaList')
  public getUpdatedAuditJBICriteriaList!: (auditId: number) => void;

  @State((state: RootState) => state.audits.auditDetail)
  public auditDetail!: AuditDetail;

  @State((state: RootState) => state.audits.apiState.getAuditDetail.success)
  public getAuditDetailApiStateSuccess!: boolean;

  @State((state: RootState) => state.audits.dataCollectionSummary)
  public dataCollectionSummary!: any;

  @State((state) => state.audits.dataCollectionPeriodTitle)
  public dataCollectionPeriodTitle!: any;

  get auditId(): number {
    return +this.$route.params.auditId;
  }

  public handleUpdatedAuditStatus(status: AuditStatus) {
    this.isAuditArchived = Boolean(status === AuditStatus.ARCHIVED);
  }

  public updateAuditSettingInfo(newValue: any) {
    this.auditSettingInfo = [];
    this.auditSettingInfo = {
      name: newValue.name,
      description: newValue.description,
      enableGrip: newValue.enableGrip,
      startDate: newValue.startDate,
      endDate: newValue.endDate,
    };
  }

  public mounted(): void {
    this.getAuditDetail(this.auditId);
  }

  @Watch('$route')
  public watchRoute(to: any, from: any) {
    if (from.hash === '#submitRecord') {
      this.showDataCollectionTemplate = false;
    }
  }

  public changeActiveTabBasedOnUrl() {
    let routeTabIndex;
    if (this.currentRouteHash === '#dataHistory') {
      routeTabIndex = tabNames.findIndex(
        (value) => value === '#dataCollection',
      );
    } else {
      routeTabIndex = tabNames.findIndex(
        (value) => value === this.currentRouteHash,
      );
    }
    const samplingTabIndex = tabNames.findIndex(
      (value) => value === '#sampling',
    );
    const dashboardTabIndex = tabNames.findIndex(
      (value) => value === '#dashboard',
    );

    if (routeTabIndex === -1) {
      this.currentTab = this.auditDetail.isUserAuthorizedToAccessAllSites
        ? samplingTabIndex
        : dashboardTabIndex;
      this.$router.replace(`${this.$route.path}${tabNames[this.currentTab]}`);
    } else if (
      routeTabIndex === samplingTabIndex &&
      !this.auditDetail.isUserAuthorizedToAccessAllSites
    ) {
      this.currentTab = dashboardTabIndex;
      this.$router.replace(`${this.$route.path}${tabNames[this.currentTab]}`);
    } else {
      this.currentTab = routeTabIndex;
    }

    this.renderTabComponent();
  }

  public renderTabComponent(updateKey: boolean = false) {
    switch (tabNames[this.currentTab]) {
      case '#criteria':
        this.showCriteriaTab = true;
        if (updateKey) {
          this.criteriaTabKey += 1;
        }
        break;
      case '#sampling':
        this.showSamplingTab = true;
        if (updateKey) {
          this.samplingTabKey += 1;
        }
        break;
      case '#dataCollection':
        this.showDataCollectionTab = true;
        if (updateKey) {
          this.dataCollectionTabKey += 1;
        }
        break;
      case '#grip':
        this.showGripTab = true;
        if (updateKey) {
          this.auditGripTabKey += 1;
        }
        break;
      case '#report':
        this.showReportTab = true;
        if (updateKey) {
          this.reportTabKey += 1;
        }
        break;
      case '#settings':
        this.showSettingsTab = true;
        if (updateKey) {
          // TODO: quick hack for reset form when navigate away from setting tab
          // proper way is to reset the form if not yet save or implement dialog on page leave
          this.settingsTabKey += 1;
        }
        break;
      default:
        if (this.currentRouteHash === '#dataHistory') {
          this.currentTab = 4;
          this.showDataCollectionTab = true;
          if (updateKey) {
            this.dataCollectionTabKey += 1;
          }
        }
        break;
    }
  }

  public tabChange(): void {
    this.isLoading = true;
    this.getAuditDetail(this.auditId);
    this.$router.replace({ hash: tabNames[this.currentTab] });
    if (tabNames[this.currentTab] === '#criteria') {
      this.getUpdatedAuditJBICriteriaList(this.auditId);
    }
    this.renderTabComponent(true);
  }

  public changeActiveTab(newValue: string) {
    this.$router.replace({ hash: newValue });
    this.currentTab = 7;
    this.changeActiveTabBasedOnUrl();
  }

  public checkUserAccess(auditDetail: AuditDetail) {
    if (!this.userEmail) {
      return false;
    }
    return auditDetail.userRoleInAudit === UserRoleInAudit.isOwner ||
      auditDetail.userRoleInAudit === UserRoleInAudit.isProjectCoOrdinator
      ? true
      : false;
  }

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  public handleShowDataCollectionTemplate(auditCriteriaId?: number) {
    this.showDataCollectionTemplate = true;
    this.singleAuditCriteriaId = auditCriteriaId;
    this.editDataCollectionRecord = null;
  }

  public closeDataCollectionTemplate() {
    this.showDataCollectionTemplate = false;
    this.currentTab = 4;
    this.singleAuditCriteriaId = undefined;
    this.editDataCollectionRecord = null;
  }

  public handleEditDataCollectionRecord(rowData: any) {
    this.editDataCollectionRecord = rowData;
    this.showDataCollectionTemplate = true;
    this.singleAuditCriteriaId = undefined;
  }

  @Watch('getAuditDetailApiStateSuccess')
  @isTruthy
  public watchGetAuditDetailSuccess(): void {
    this.getUpdatedAuditJBICriteriaList(this.auditId);
    if (this.auditDetail.status === 'Deleted') {
      this.$router.push({
        name: 'project-details',
        params: {
          projectId: this.auditDetail.project.id.toString(),
        },
      });
    }
    this.isProjectDetailsLoading = true;
    this.getProjectDetail(this.auditDetail.project.id);
  }

  @Watch('getProjectDetailApiStateSuccess')
  @isTruthy
  public watchGetProjectDetailSuccess() {
    if (this.projectDetail.status === 'Deleted') {
      this.$router.push({
        name: 'project-me',
      });
    } else if (
      this.auditDetail.status === 'Deleted' ||
      !this.checkUserAccess(this.auditDetail)
    ) {
      this.$router.push({
        name: 'project-details',
        params: {
          projectId: this.auditDetail.project.id.toString(),
        },
      });
    }

    this.renderComponent = false;
    this.auditSettingInfo = {
      name: this.auditDetail.name,
      description: this.auditDetail.description,
      enableGrip: this.auditDetail.enableGrip,
      startDate: this.auditDetail.startDate,
      endDate: this.auditDetail.endDate,
    };
    this.changeActiveTabBasedOnUrl();
    this.$nextTick(() => {
      this.renderComponent = true;
    });

    this.isLoading = false;
    this.showAuditPage = true;
    this.isProjectDetailsLoading = false;
    this.isAuditArchived = Boolean(
      this.projectStatus === 'Archived' ||
        this.currentAuditStatus === AuditStatus.ARCHIVED,
    );
    this.projectOwner = this.projectDetail.user;
  }
}
</script>
<style lang="scss" module>
.backContainer a {
  color: #444f51;
  font-size: 20px;
  font-weight: 400;
}
.tabLabel nav li a {
  padding-left: 0;
  color: #9f9f9f;
  font-size: 16px;
  font-weight: 700;
  padding-right: 0px;
}
.tabLabel nav li a:hover {
  color: #00205b;
}
.tabLabel nav li {
  padding-right: 2rem;
}
.auditTitle {
  color: #00205b;
  font-size: 40px;
  font-weight: 700;
}
.projectTitle {
  color: #444f51;
  font-size: 20px;
}
.tableName {
  color: #444f51;
}
.tableContainer {
  padding: 5px;
  background-color: #fbfbfb;
  border: 1px solid #dfdfdf;
  border-radius: 4px;
  thead {
    background-color: #fbfbfb;
  }
  tbody {
    background-color: #fbfbfb;
  }
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  width: auto;
  padding: 0 calc(1em + 0.25em);
  bottom: 15px;
}
.headerContainer {
  padding: 8px 0px 1rem 0px;
}
.headerRightIcon {
  padding-right: 15px;
  color: #00205b;
  font-size: 14px;
}
.headerRightText {
  color: #444f51;
}
.title {
  color: #444f51;
  font-size: 14px;
}

.tabLabel > section {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.noDataMessageContainer {
  text-align: center;
  vertical-align: middle;
  height: 100%;
}
.noDataMessage {
  line-height: 30;
  color: #9f9f9f;
  font-size: 18px;
  font-weight: bold;
}
.tabItem {
  padding-top: 22px;
}
.settingsTab {
  padding-top: 14px;
}
.projectTitleContainer {
  padding-top: 10px;
  padding-left: 19px;
  display: flex;
  justify-content: flex-start;
  div:first-child {
    margin-right: 27px;
  }
}
.headerStartText {
  color: #00205b;
  font-size: 18px;
  font-weight: 700;
}
.projectCriteriaEditContainer {
  padding: 0rem 11rem;
}
.addItem {
  color: #21b5ea;
  font-weight: 400;
}
.auditInfoSmallText {
  color: #444f51;
  font-size: 16px;
  text-transform: capitalize;
}
.auditSettingsEditContainer {
  width: 680px;
  margin-right: auto;
  margin-left: 20px;
}
.projectSamplingEditContainer {
  padding: 0rem 0rem;
}
.auditTitle {
  margin-bottom: 8px;
}
.auditPageTopContainer {
  margin-top: 5px;
  padding-top: 8px;
}
.activityLogContainer {
  vertical-align: middle;
  height: 100%;
}
.deleteButton {
  color: #ff7171;
}
.deleteButton:hover {
  color: red;
}
.actionDataCollectionAudit {
  background: unset;
  border: unset;
  padding-left: 0px;
  margin-bottom: 30px;
  span {
    font-size: 14px;
  }
}
.stopDataCollectionAudit {
  color: #ff7171;
  &:hover {
    span {
      color: #ff7171;
    }
  }
}
.resumeDataCollectionAudit {
  color: #00205b;
  &:hover {
    span {
      color: #00205b;
    }
  }
}
.customCalendarIcon {
  i::before {
    padding-top: 13px;
    padding-bottom: 12px;
    width: 15px;
    margin-right: 10px;
  }
}
.calendarContainer {
  -webkit-box-flex: 0;
  display: block;
  flex: none;
  width: 50%;
}
</style>
